import { FRACTION_ZERO } from "@aprao/common/src/math/fraction.js";
import {
  formatDecimalNumber,
  formatPercentage,
} from "@aprao/common/src/numberFormats.js";
import type { ResidentialSalesGroupFacade } from "@aprao/model/src/stores/revenue/ResidentialSalesGroupFacade.js";
import type { SalesTaxConfigurationFacade } from "@aprao/model/src/stores/SalesTaxConfigurationFacade.js";
import type { TaxFacade } from "@aprao/model/src/stores/TaxFacade.js";
import { observer } from "mobx-react-lite";
import { mapForNullable } from "option-t/nullable";

import { useI18n } from "../../../common/ApraoI18n.js";
import { useNumberUnits } from "../../../common/useNumberUnits.js";
import { buildRevenueItemDescription } from "../../../i18n/buildRevenueItemDescription.js";
import { buildSalesTaxName } from "../../../i18n/buildSalesTaxName.js";
import { SimpleTableRow, SimpleTableSection } from "../../SimpleTableRow.js";

export const ResidentialTableSection = observer(
  function ResidentialTableSection(props: {
    label: string;
    revenueGroup: ResidentialSalesGroupFacade;
    salesTaxConfiguration: SalesTaxConfigurationFacade | null;
    tax: TaxFacade;
  }) {
    const { label, tax, revenueGroup, salesTaxConfiguration } = props;

    const { msg, effectiveLocale } = useI18n();
    const { lengthUnit, currency } = useNumberUnits();

    return (
      <SimpleTableSection>
        <SimpleTableRow
          isHeader
          cells={[
            label,
            null,
            null,
            `${currency.symbol}/${lengthUnit.symbol}²`,
            `${lengthUnit.symbol}² (${msg("global.nia")})`,
            `Total (${currency.symbol})`,
          ]}
        />
        {revenueGroup.items.map((item, itemIdx) => (
          <SimpleTableRow
            key={itemIdx}
            cells={[
              buildRevenueItemDescription(effectiveLocale, item.$model),
              null,
              null,
              mapForNullable(
                item.salePrice.relativesSet.getRelative("ratioArea"),
                formatDecimalNumber,
              ),
              mapForNullable(item.aggregateArea, formatDecimalNumber),
              mapForNullable(item.aggregateValue, formatDecimalNumber),
            ]}
            data-testid="rts__item-row"
          />
        ))}
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          cells={[
            "Gross value",
            null,
            null,
            mapForNullable(
              revenueGroup.averageValuePerArea,
              formatDecimalNumber,
            ) ?? "N/A",
            formatDecimalNumber(revenueGroup.areaTotal ?? FRACTION_ZERO),
            formatDecimalNumber(revenueGroup.valueGross ?? FRACTION_ZERO),
          ]}
        />
        <SimpleTableRow
          cells={[
            "Costs",
            null,
            formatPercentage(revenueGroup.costAndFeesOnValue ?? FRACTION_ZERO),
            null,
            null,
            formatDecimalNumber(revenueGroup.costsTotal ?? FRACTION_ZERO),
          ]}
        />
        {salesTaxConfiguration && (
          <SimpleTableRow
            cells={[
              buildSalesTaxName(salesTaxConfiguration.$model),
              null,
              formatPercentage(tax.gdvEffectiveSalesTaxRate ?? FRACTION_ZERO),
              null,
              null,
              formatDecimalNumber(
                tax.getTaxAmountByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
              ),
            ]}
          />
        )}
        <SimpleTableRow
          bold
          borderBottom
          borderTop
          thickerBorder
          cells={[
            "Net value",
            null,
            null,
            null,
            null,
            formatDecimalNumber(
              tax.getValueNetByRevenueGroup(revenueGroup) ?? FRACTION_ZERO,
            ),
          ]}
        />
      </SimpleTableSection>
    );
  },
);
